/**
 * Rich-content body for the Allocatable Budget info popover. Source of
 * truth for the user-facing explanation of the budget allocation
 * algorithm — referenced from both the BudgetOptimizer card and the
 * Automated Budget Optimization intro card. Iterate copy here.
 *
 * See docs/ALLOCATABLE_BUDGET_TOOLTIP_COPY.md for design history and
 * the per-section rationale (audit §5.9.1–§5.9.2 underpins the math
 * appendix).
 */
export const AllocatableBudgetTooltipContent = () => (
  <div className="space-y-3">
    <div>
      <p className="text-lg font-bold text-slate-900 mb-1">How we allocate your budget</p>
      <p className="text-slate-700">
        Each week we rebalance your total budget across campaigns, gradually shifting more toward those earning more revenue per dollar spent (their ROAS, measured over the past 28 days). Moves are small and bounded — we never reallocate more than a fraction of the total at a time — and every campaign keeps a minimum share of your budget, so nothing is ever zeroed out.
      </p>
    </div>

    <div>
      <p className="font-semibold text-slate-900">How the direction is chosen</p>
      <p className="text-slate-700 mt-1">
        Each campaign's pull is its ROAS as a share of total ROAS across the campaigns you've opted in. Higher-than-average ROAS attracts more budget; campaigns with lower-than-average ROAS give some up.
      </p>
    </div>

    <div>
      <p className="font-semibold text-slate-900">Why this doesn't pile everything onto a single campaign</p>
      <p className="text-slate-700 mt-1">
        You might expect this to keep concentrating budget on whichever campaign has the best ROAS until almost everything sits there. It doesn't — because ROAS itself reacts to spend. As a campaign gets more budget, it bids into thinner inventory (cost per click rises) and reaches past its highest-intent audience (conversions per click falls). Realized ROAS drifts down. Next week we re-measure, and if that campaign no longer looks as dominant, the algorithm shifts less toward it. Over time the allocation settles at a balance that improves overall return on ad spend.
      </p>
    </div>

    <div>
      <p className="font-semibold text-slate-900">Why this is safe</p>
      <p className="text-slate-700 mt-1">
        Three things keep the system stable: small weekly steps, the natural ROAS feedback above, and a minimum-share floor on every campaign. Together they push toward better performance through gradual, deliberate moves.
      </p>
    </div>

    <div className="pt-3 mt-3 border-t border-gray-200">
      <p className="font-semibold text-slate-900">The math, for the curious</p>
      <p className="text-slate-700 mt-1">
        Under the hood, the rebalancer is a small per-run adjustment rule. Each campaign has a <em>share</em> of your total budget — call it <code className="text-xs bg-slate-100 px-1 rounded">sᵢ</code> for campaign i — and those shares always add up to 100%. Each weekly run nudges these shares a small distance in a chosen direction, respecting the per-campaign minimum and keeping the total at 100%.
      </p>
      <p className="text-slate-700 mt-2">
        The direction we nudge in is the per-campaign weight <code className="text-xs bg-slate-100 px-1 rounded">ROAS_i / total_ROAS</code>. Each campaign's revenue is proportional to its share times its ROAS — <code className="text-xs bg-slate-100 px-1 rounded">sᵢ · ROAS_i</code> — so total revenue is proportional to
      </p>
      <p className="text-center italic text-slate-700 my-2">
        f(s) = Σᵢ sᵢ · ROAS_i,
      </p>
      <p className="text-slate-700">
        and each weekly run is a <strong>projected-gradient step</strong>: subtract the mean of <code className="text-xs bg-slate-100 px-1 rounded">ROAS_i / total_ROAS</code> (so the step preserves the 100% total) and move a fixed L1 distance in that direction — the steepest ascent of f subject to the shares summing to 1. Combined with the weekly ROAS re-measurement described above, the rebalancer converges toward an allocation where measured ROAS values are roughly equalized across campaigns.
      </p>
    </div>
  </div>
);
